
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";

import { controller, endpoints } from "../../api/actions/api";
import { AppContext } from "../../App";
import { Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import ReportPrintButton from "./ReportPrintButton";
import { formatCurrency } from "../../hooks/Currency";
import { formatDateTime } from "../../hooks/DateFormat";

const ExpenseReport = (props) => {
  const { t } = useTranslation();
  const { _store, _getById } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [expenses, setExpenses] = useState([]);

  // Period filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  // Load expenses
  useEffect(() => {
    setExpenses([]);

    if (!_store?.id) return;

    setLoader(true);

    const onComplete = (res) => {
      setLoader(false);

      if (res?.status === 200 && Array.isArray(res.result)) {
        setExpenses(res.result);
      } else {
        setExpenses([]);
      }
    };

    _getById(
      endpoints.Expense + controller.GetByStoreId,
      _store.id,
      null,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Filter expenses by addDate
  const filteredExpenses = useMemo(() => {
    if (invalidRange) return [];

    return expenses.filter((exp) => {
      if (!fromDate && !toDate) return true;

      if (!exp.addDate) return false;

      const expenseDate = new Date(exp.addDate);

      if (Number.isNaN(expenseDate.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(`${fromDate}T00:00:00`);

        if (expenseDate < from) {
          return false;
        }
      }

      if (toDate) {
        const to = new Date(`${toDate}T23:59:59.999`);

        if (expenseDate > to) {
          return false;
        }
      }

      return true;
    });
  }, [expenses, fromDate, toDate, invalidRange]);

  // Prepare expense table data
  const tableData = useMemo(() => {
    return filteredExpenses.map((exp) => ({
      ...exp,

      addDate: formatDateTime(exp.addDate),

      expenseType: exp.expenseCat?.name || "",

      amount: formatCurrency(exp.amount, _store),

      paidBy: [
        exp.user?.firstName,
        exp.user?.lastName,
      ]
        .filter(Boolean)
        .join(" "),
    }));
  }, [filteredExpenses, _store]);

  // Shared columns for screen and print
  const columns = [
    { id: "addDate", label: t("date") },
    { id: "expenseType", label: t("expenseType") },
    { id: "note", label: t("detailsNote") },
    { id: "amount", label: t("paid") },
    { id: "paidBy", label: t("paidBy") },
  ];

  return (
    <>
      {/* Date filters and Print Preview button */}
      {/* These controls are not included in printing */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("expenseReport")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{ shrink: true }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("expenseReport")}
        />
      </Box>

      {/* Normal screen report */}
      {/* Search and Pagination remain enabled */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("expenseReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        <Table
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={tableData}
        />
      </Box>

      {/* Separate printable report - all rows */}
      <Box
        ref={reportRef}
        className="expense-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        {/* Report title */}
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("expenseReport")}
        </Typography>

        {/* Selected date range */}
        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        {/* Print all records without Search/Pagination */}
        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={tableData}
        />
      </Box>
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(ExpenseReport);
